import type { SimplePrintLine } from '@/print-engine/adapters/simple-document';
import { formatMoney } from '@/utils/format';
import {
  normalizeSections,
  seedTechnicalSections,
} from '@/components/epc/commercial-ux/estimateProposalSubmissionReadiness';
import {
  parseCommercialDeviations,
} from '@/components/epc/commercial-ux/commercialDeviations';

function money(n: unknown, currency: string) {
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  return formatMoney(v, {
    currency: currency || 'AED',
    locale: 'en-US',
    display: 'code_prefix',
    separator: ' ',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export type EpcProposalPrintBundle = {
  tender: Record<string, unknown>;
  proposal: Record<string, unknown>;
  basisStatus?: string | null;
  estimateNumber?: string | null;
  revisionLabel?: string | null;
};

export function buildEpcProposalPrintMeta(bundle: EpcProposalPrintBundle) {
  const p = bundle.proposal;
  const t = bundle.tender;
  const currency = String(p.currency || t.currency || 'AED');
  return [
    { label: 'Proposal', value: String(p.proposal_number || p.title || p.id || '') },
    { label: 'Tender', value: String(t.tender_number || t.title || '') },
    { label: 'Client', value: String(t.client_name || t.clientName || '') },
    { label: 'Status', value: String(p.status || '') },
    { label: 'Revision', value: String(p.revision || 1) },
    { label: 'Currency', value: currency },
    {
      label: 'Commercial value',
      value: money(p.commercial_value ?? p.commercialValue, currency),
    },
    bundle.basisStatus ? { label: 'Estimate basis', value: String(bundle.basisStatus) } : null,
    bundle.estimateNumber
      ? {
          label: 'Estimate',
          value: `${bundle.estimateNumber}${bundle.revisionLabel ? ` · ${bundle.revisionLabel}` : ''}`,
        }
      : null,
  ].filter(Boolean) as Array<{ label: string; value: string }>;
}

export function buildEpcProposalPrintLines(bundle: EpcProposalPrintBundle): SimplePrintLine[] {
  const p = bundle.proposal;
  const meta = (p.meta_json || p.metaJson || {}) as Record<string, unknown>;
  const terms = (meta.commercial_terms || {}) as Record<string, unknown>;
  const tech = normalizeSections(p.technical_sections_json ?? p.technicalSectionsJson);
  const techRows = tech.length
    ? tech
    : seedTechnicalSections().map((s) => ({ key: s.key, complete: false }));
  const deviations = parseCommercialDeviations(meta);
  const lines: SimplePrintLine[] = [];
  let n = 0;

  lines.push({
    id: 'sec-tech',
    lineNumber: '',
    description: '— TECHNICAL SECTIONS —',
    cells: { type: '', status: '', detail: '' },
  });
  for (const row of techRows) {
    n += 1;
    lines.push({
      id: `tech-${row.key}`,
      lineNumber: n,
      description: row.key,
      cells: {
        type: 'Technical',
        status: row.complete ? 'Complete' : 'Missing',
        detail: '',
      },
    });
  }

  lines.push({
    id: 'sec-terms',
    lineNumber: '',
    description: '— COMMERCIAL TERMS —',
    cells: { type: '', status: '', detail: '' },
  });
  const termEntries = [
    ['Payment terms', terms.payment_terms || terms.paymentTerms],
    ['Validity days', terms.validity_days || terms.validityDays],
    ['Delivery / duration', terms.delivery || terms.duration],
    ['Retention', terms.retention],
    ['LD / penalties', terms.ld || terms.liquidated_damages],
  ];
  for (const [label, value] of termEntries) {
    if (value == null || value === '') continue;
    n += 1;
    lines.push({
      id: `term-${label}`,
      lineNumber: n,
      description: String(label),
      cells: {
        type: 'Terms',
        status: '',
        detail: String(value),
      },
    });
  }

  lines.push({
    id: 'sec-dev',
    lineNumber: '',
    description: '— COMMERCIAL DEVIATIONS —',
    cells: { type: '', status: '', detail: '' },
  });
  if (!deviations.length) {
    lines.push({
      id: 'dev-none',
      lineNumber: '',
      description: 'No commercial deviations recorded',
      cells: { type: '', status: '', detail: '' },
    });
  } else {
    for (const d of deviations) {
      n += 1;
      lines.push({
        id: `dev-${n}`,
        lineNumber: n,
        description: String(d.clause || 'Deviation'),
        cells: {
          type: 'Deviation',
          status: String(d.status || d.risk || ''),
          detail: String(d.bid_position || d.client_requirement || d.deviation || ''),
        },
      });
    }
  }

  return lines;
}
